import React, { createContext, useContext, useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { CostCenterItem, Project } from './Types'
import { apiSetPrepareAllScopes } from './Api'

// The active role's scope: the (cost-centre, project) pairs it owns, plus Attestera's "show all
// scopes" preference. LocationSelect uses the scope keys to offer only in-scope options for
// *picking*; name resolution / display always uses the full tree. The allScopes flag (persisted per
// user) lets a scoped role opt into seeing every location in the ATTEST pickers only — Översikt has
// its own separate preference, and income pickers stay hard-scoped (backend-enforced).
type ScopeContextValue = {
    keys: ReadonlySet<string> | null,
    allScopes: boolean,
    toggleAllScopes: () => void,
}
const ScopeContext = createContext<ScopeContextValue | null>(null)

export function scopeKey(costCentreId: string, projectId: string): string {
    return `${costCentreId} ${projectId}`
}

// Restrict a cost-centre/project tree to the given pair keys for *picking*, always keeping the
// currently-selected pair even if out of scope (so an existing choice never disappears — only new
// picks are limited). null keys ⇒ no filtering. Shared by the expense and income row editors so
// both scope-filter identically.
export function scopedTree<P extends Project>(
    costCenterItems: CostCenterItem<P>[],
    keys: ReadonlySet<string> | null,
    currentCostCentreId: string | null,
    currentProjectId: string | null,
): CostCenterItem<P>[] {
    if (keys === null) return costCenterItems
    const keep = (costCentreId: string, projectId: string) =>
        keys.has(scopeKey(costCentreId, projectId))
        || (costCentreId === currentCostCentreId && projectId === currentProjectId)
    return costCenterItems
        .map(item => ({ ...item, projects: item.projects.filter(p => keep(item.cost_center_item_id, p.project_id)) }))
        .filter(item => item.projects.length > 0)
}

export function ScopeProvider({ scope, initialAllScopes, children }: {
    scope: readonly (readonly [string, string])[],
    initialAllScopes: boolean,
    children: React.ReactNode,
}) {
    const keys = useMemo(() => new Set(scope.map(([cc, project]) => scopeKey(cc, project))), [scope])
    const [allScopes, setAllScopes] = useState(initialAllScopes)
    // Persist only on an actual toggle (not on mount), mirroring the config editors' changedRef
    // pattern. The PUT is fire-and-forget — it's a preference, and an occasional failure just means
    // the next load reflects the stored value.
    const changedRef = useRef(false)
    const toggleAllScopes = useCallback(() => { changedRef.current = true; setAllScopes(prev => !prev) }, [])
    useEffect(() => {
        if (changedRef.current) { changedRef.current = false; void apiSetPrepareAllScopes(allScopes) }
    }, [allScopes])

    const value = useMemo(() => ({ keys, allScopes, toggleAllScopes }), [keys, allScopes, toggleAllScopes])
    return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
}

// Whether any (cost-centre, project) pair is selectable under `keys` (null ⇒ everything). Lets a
// row editor show an explanatory empty-state instead of a silent, unusable picker.
export function hasScopedPair<P extends Project>(costCenterItems: CostCenterItem<P>[], keys: ReadonlySet<string> | null): boolean {
    return costCenterItems.some(item => item.projects.some(p => keys === null || keys.has(scopeKey(item.cost_center_item_id, p.project_id))))
}

// Why nothing is selectable in an *income* picker, or null when something is.
//
// The income tree is built from projects that have an income account mapped, and the ones without
// are dropped before any scope filtering happens (see get_cost_center_items_with_income_accounts).
// So an empty picker means "no account mapped" at least as often as "no permission", and these
// views used to report only the second. Someone with perfectly good scope was told they had none,
// and went looking through Roller for a permission they already had.
//
// The expense side has said the right thing for a while; this is the same distinction for income.
export function noIncomePairReason<P extends Project>(
    costCenterItems: CostCenterItem<P>[],
    keys: ReadonlySet<string> | null,
): string | null {
    if (hasScopedPair(costCenterItems, keys)) return null
    // Nothing anywhere has an income account: certain, and nothing to do with permissions.
    if (!hasScopedPair(costCenterItems, null)) {
        return 'Inga projekt har något intäktskonto kopplat ännu. Be en administratör koppla ett '
            + 'intäktskonto (Inställningar → Företag).'
    }
    // Something is mapped, but none of it is yours — which of the two causes it is cannot be told
    // from here, because a project without an account is already gone from the tree.
    return 'Inget kostnadsställe och projekt är valbart här. Antingen saknar dina projekt '
        + 'intäktskonto (Inställningar → Företag), eller så saknar du behörighet till något som '
        + 'har det (Inställningar → Roller).'
}

// The in-scope pair keys, or null when there's no provider (⇒ don't filter). Deliberately does NOT
// bypass on `allScopes`: income pickers (customer invoice, Swish) use this and are hard-enforced by
// the backend, so they must always stay scoped. The "show all" opt-in is applied where it's allowed
// — the expense pickers (via pickableKeys) and the read-only Överblick (server-side).
export function useScopeKeys(): ReadonlySet<string> | null {
    return useContext(ScopeContext)?.keys ?? null
}

// Attestera's "show all scopes" preference state + flipper (persisted per user). Consumed only by
// the attest pickers; Översikt has its own independent preference. Safe to call outside a provider.
export function useAllScopes(): { allScopes: boolean, toggleAllScopes: () => void } {
    const ctx = useContext(ScopeContext)
    return { allScopes: ctx?.allScopes ?? false, toggleAllScopes: ctx?.toggleAllScopes ?? (() => {}) }
}
